import { apiClient } from "../base-client";

export interface BuilderPartnerProperty {
  communityName: string | null;
  lotNumber: string | null;
  addressLine1: string | null;
  city: string | null;
  state: string | null;
  zipcode: string | null;
  /** The home plan's name, e.g. "Bridgewater". */
  homeModel: string | null;
  /** The plan's exterior style, e.g. "Craftsman". Join with `homeModel` for display. */
  homePlanExterior: string | null;
  elevation: string | null;
}

/**
 * Builder-partner context for a design project — the co-brand identity and
 * partner-scoped project data (palette choice, Pinterest board, matched
 * property, site plan). `partnerKey` / `partnerDisplayName` are null for a
 * regular project or before the buyer match resolves.
 */
export interface BuilderPartnerContext {
  matchStatus:
    | "MATCHED"
    | "CONFIRMED"
    | "NEEDS_SUPPORT"
    | "NO_MATCH"
    | "MULTIPLE_MATCHES";
  confirmedAt: string | null;
  needsSupportAt: string | null;
  selectedPaletteId: string | null;
  pinterestUrl: string | null;
  communityId: string | null;
  partnerKey: string | null;
  partnerDisplayName: string | null;
  property: BuilderPartnerProperty | null;
  /** Signed URL for the site plan PDF (LOT_PLAN document). */
  sitePlanUrl: string | null;
  /** Signed URL for the site plan preview image (preferred on the Property screen). */
  sitePlanImageUrl: string | null;
  /** Signed URL for the "Your Home" home-model rendering image. */
  homeModelImageUrl: string | null;
  /** Signed URL for the home-model detail PDF. */
  homeModelDocUrl: string | null;
}

export interface UpdateBuilderPartnerContextInput {
  action?: "confirm" | "not_my_home";
  selectedPaletteId?: string;
  pinterestUrl?: string;
}

const contextPath = (accountId: string, designProjectId: string) =>
  `accounts/${accountId}/design-projects/${designProjectId}/partner-context`;

/**
 * Reads the builder-partner context for a design project. Returns null for a
 * regular (non-partner) project — the endpoint answers 200 with an empty body,
 * so callers can run this on any project without treating "no partner" as an
 * error.
 */
export async function getBuilderPartnerContext(
  accountId: string,
  designProjectId: string,
): Promise<BuilderPartnerContext | null> {
  const response = await apiClient.get<BuilderPartnerContext | null>(
    contextPath(accountId, designProjectId),
  );
  // An empty body parses as "" — normalize any falsy body to null.
  return response.data || null;
}

export async function updateBuilderPartnerContext(
  accountId: string,
  designProjectId: string,
  input: UpdateBuilderPartnerContextInput,
): Promise<BuilderPartnerContext> {
  const { data } = await apiClient.put<BuilderPartnerContext>(
    contextPath(accountId, designProjectId),
    input,
  );
  return data;
}

export async function resolveBuilderPartnerContext(
  accountId: string,
  designProjectId: string,
): Promise<BuilderPartnerContext> {
  const { data } = await apiClient.post<BuilderPartnerContext>(
    `${contextPath(accountId, designProjectId)}/resolve`,
  );
  return data;
}

/**
 * Finalize a homeowner-uploaded site plan (Property fallback): wrap the
 * already-uploaded media (from uploadPhotos with r2Target "partner-docs") in a
 * LOT_PLAN document and attach it to the lot. Idempotent per media id.
 */
export async function confirmSitePlanUpload(
  accountId: string,
  designProjectId: string,
  mediaId: string,
): Promise<BuilderPartnerContext> {
  const { data } = await apiClient.post<BuilderPartnerContext>(
    `${contextPath(accountId, designProjectId)}/site-plan/confirm`,
    { mediaId },
  );
  return data;
}
